// B6 — Compliance tab due-date strip: overdue / due-soon counts for the
// legal cadence items (Legal Ops cadence source types + the annual
// legal-review tickler), rendered through the shared AttentionStrip
// (count chips → expandable list).

import { useMemo } from "react";
import { useLoanDueDates } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useDateFormatter } from "@/utils/datetime";
import type { ComplianceCalendarItemDto } from "@/api/portfolioAdmin/types";
import { AttentionStrip, type AttentionChip } from "../common/AttentionStrip";
import { daysUntil } from "../common/colorBand";
import { LEGAL_OPS_SOURCE_TYPES } from "./UpcomingOverdueSection";

function isLegalCadenceItem(item: ComplianceCalendarItemDto): boolean {
  return (
    LEGAL_OPS_SOURCE_TYPES.has(item.sourceType) ||
    item.sourceType === "legal-review-annual"
  );
}

function isOpenStatus(status: string): boolean {
  const s = status.toLowerCase();
  return s !== "completed" && s !== "waived";
}

export function LegalCadenceStrip({ loanId }: { loanId: string }) {
  const { data } = useLoanDueDates(loanId);
  const { tz } = useDateFormatter();

  const chips = useMemo<AttentionChip[]>(() => {
    const open = (data ?? []).filter(
      (i) => isLegalCadenceItem(i) && isOpenStatus(i.status),
    );
    const toItem = (i: ComplianceCalendarItemDto) => ({
      id: i.id,
      title: i.title,
      date: i.dueDate,
    });
    const overdue = open.filter((i) => (daysUntil(i.dueDate) ?? 1) < 0);
    const dueSoon = open.filter((i) => {
      const days = daysUntil(i.dueDate);
      return days !== null && days >= 0 && days <= 30;
    });
    return [
      {
        id: "overdue",
        label: "Overdue",
        count: overdue.length,
        tone: "error",
        items: overdue.map(toItem),
      },
      {
        id: "due-soon",
        label: "Due within 30 days",
        count: dueSoon.length,
        tone: "warning",
        items: dueSoon.map(toItem),
      },
    ];
  }, [data, tz]);

  return <AttentionStrip chips={chips} testId="legal-cadence-strip" />;
}
